<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <div class="toolbar-card mb-4">
        <div class="toolbar-left">
          <h4 class="mb-1">{{ $t("promotion_page.list_title") }}</h4>
          <p class="text-muted mb-0">
            {{ $t("promotion_page.list_description") }}
          </p>
        </div>
        <div class="toolbar-right">
          <div class="search-shell">
            <input
              :value="searchQuery"
              type="text"
              class="form-control toolbar-search"
              :placeholder="$t('promotion_page.search_placeholder')"
              @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            />
            <i class="ri-search-line search-icon"></i>
          </div>
          <button
            v-if="canCreate"
            class="btn style-one btn-sm d-flex align-items-center gap-2"
            @click="$emit('create')"
          >
            <i class="ri-add-circle-line"></i>
            <span>{{ $t("promotion_page.create_button") }}</span>
          </button>
        </div>
      </div>

      <div class="stats-grid mb-4">
        <div class="stat-card">
          <span class="stat-label">{{ $t("promotion_page.total_promotions") }}</span>
          <strong class="stat-value">{{ stats.total }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{ $t("promotion_page.status_active") }}</span>
          <strong class="stat-value text-success">{{ stats.active }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{ $t("promotion_page.customer_specific") }}</span>
          <strong class="stat-value text-primary">{{ stats.customerSpecific }}</strong>
        </div>
        <div class="stat-card">
          <span class="stat-label">{{ $t("promotion_page.global") }}</span>
          <strong class="stat-value text-warning">{{ stats.global }}</strong>
        </div>
      </div>

      <a-table
        :columns="columns"
        :data-source="promotions"
        :row-key="'id'"
        :pagination="paginationConfig"
        :loading="loading"
        :locale="{ emptyText: $t('promotion_page.no_promotions_found') }"
        class="promotion-table"
      >
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'code'">
            <span class="code-pill">{{ text || $t("promotion_page.not_available") }}</span>
          </template>
          <template v-else-if="column.key === 'customer'">
            <span class="customer-pill" :class="record.customer_id ? 'customer-specific' : 'customer-all'">
              {{ record.customer_name || $t("promotion_page.all_customers") }}
            </span>
          </template>
          <template v-else-if="column.key === 'discount'">
            <div class="table-copy">
              <strong>{{ formatPromotionValue(record) }}</strong>
              <small>{{ record.discount_type === "percentage" ? $t("promotion_page.percentage_auto_discount") : $t("promotion_page.fixed_auto_discount") }}</small>
            </div>
          </template>
          <template v-else-if="column.key === 'validity'">
            <div class="table-copy">
              <strong>{{ formatPromotionWindow(record) }}</strong>
              <small>{{ formatPromotionDays(record.valid_day) }}</small>
            </div>
          </template>
          <template v-else-if="column.key === 'status'">
            <span class="status-badge" :class="isActive(record) ? 'status-active' : 'status-inactive'">
              {{ isActive(record) ? $t("promotion_page.status_active") : $t("promotion_page.status_inactive") }}
            </span>
          </template>
          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2">
              <button v-if="canUpdate" class="icon-btn" :title="$t('promotion_page.edit')" @click="$emit('edit', record)">
                <i class="ri-pencil-line"></i>
              </button>
              <button
                v-if="canDelete"
                class="icon-btn danger"
                :title="$t('promotion_page.delete')"
                @click="$emit('delete', record)"
              >
                <i class="ri-delete-bin-6-line"></i>
              </button>
              <span v-if="!canUpdate && !canDelete" class="text-muted small fw-semibold">
                {{ $t("promotion_page.view_only") }}
              </span>
            </div>
          </template>
          <template v-else>
            {{ text || $t("promotion_page.not_available") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { PromotionRecord } from "@/utils/promotion";

export default defineComponent({
  name: "PromotionList",
  emits: ["update:searchQuery", "create", "edit", "delete"],
  props: {
    promotions: {
      type: Array as PropType<PromotionRecord[]>,
      required: true,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    paginationConfig: {
      type: Object as PropType<Record<string, unknown>>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
    canUpdate: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    stats: {
      type: Object as PropType<{
        total: number;
        active: number;
        customerSpecific: number;
        global: number;
      }>,
      required: true,
    },
    formatPromotionValue: {
      type: Function as PropType<(record: PromotionRecord) => string>,
      required: true,
    },
    formatPromotionDays: {
      type: Function as PropType<(value?: string | null) => string>,
      required: true,
    },
    formatPromotionWindow: {
      type: Function as PropType<(record: PromotionRecord) => string>,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const columns = computed(() => [
      { title: t("promotion_page.table_code"), dataIndex: "code", key: "code", align: "center" as const, width: 160 },
      { title: t("promotion_page.table_promotion"), dataIndex: "name", key: "name", align: "center" as const, width: 220 },
      { title: t("promotion_page.table_customer"), key: "customer", align: "center" as const, width: 200 },
      { title: t("promotion_page.table_discount"), key: "discount", align: "center" as const, width: 190 },
      { title: t("promotion_page.table_validity"), key: "validity", align: "center" as const, width: 240 },
      { title: t("promotion_page.table_status"), key: "status", align: "center" as const, width: 130 },
      { title: t("promotion_page.table_action"), key: "actions", align: "center" as const, width: 140 },
    ]);

    const isActive = (record: PromotionRecord) =>
      String(record.status ?? 1) !== "0" &&
      String(record.plan_status ?? 1) !== "0";

    return {
      columns,
      isActive,
    };
  },
});
</script>

<style scoped>
.toolbar-card {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 18px;
  padding: 22px;
  border: 1px solid #edf0f7;
  border-radius: 20px;
  background: #fbfcff;
}

.toolbar-right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.search-shell {
  position: relative;
  min-width: 340px;
}

.toolbar-search {
  min-height: 54px;
  padding: 14px 48px 14px 18px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  box-shadow: none;
}

.toolbar-search:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.search-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d8aa2;
  pointer-events: none;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px;
  border: 1px solid #edf0f7;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.stat-label {
  color: #7d8aa2;
  font-size: 13px;
  font-weight: 600;
}

.stat-value {
  color: #0f172a;
  font-size: 24px;
  line-height: 1.1;
}

.promotion-table :deep(.ant-table-thead > tr > th) {
  font-weight: 700;
}

.code-pill,
.customer-pill,
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
}

.code-pill {
  background: #eef2ff;
  color: #4f46e5;
}

.customer-specific {
  background: #eff6ff;
  color: #2563eb;
}

.customer-all {
  background: #fff7ed;
  color: #ea580c;
}

.table-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.table-copy small {
  color: #7d8aa2;
}

.status-badge {
  font-size: 12px;
}

.status-active {
  background: #e8f7ee;
  color: #1f9254;
}

.status-inactive {
  background: #fdecec;
  color: #d33c43;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e3e8f5;
  background: #fff;
  color: #4f46e5;
}

.icon-btn.danger {
  color: #d33c43;
}

@media (max-width: 991px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .search-shell {
    min-width: 100%;
  }
}
</style>
